import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { 
  CloudRain, 
  Sun, 
  Cloud, 
  Wind, 
  Droplets,
  Thermometer,
  MapPin,
  RefreshCw,
  Clock,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { gardensService } from '@/services';
import { WeatherData, HourlyWeatherData } from '@/types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart,
  Bar,
} from 'recharts';

const weatherIcons: Record<string, typeof Sun> = {
  sun: Sun,
  cloud: Cloud,
  'cloud-rain': CloudRain,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Componente para gráficas horarias
function HourlyCharts({ hourlyData, title }: { hourlyData: HourlyWeatherData[]; title: string }) {
  const chartData = hourlyData.map(h => ({
    hour: `${h.hour.toString().padStart(2, '0')}:00`,
    temperatura: h.temperature,
    humedad: h.humidity,
    lluvia: h.precipitationProbability,
    precipitacion: h.precipitation,
    viento: h.windSpeed,
  }));

  return (
    <div className="space-y-6">
      <h3 className="font-display font-semibold text-lg flex items-center gap-2">
        <Clock className="w-5 h-5 text-primary" />
        {title}
      </h3>
      
      {/* Gráfica de Temperatura y Humedad */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Thermometer className="w-4 h-4 text-warning" />
            Temperatura y Humedad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="hour" 
                  tick={{ fontSize: 10 }}
                  interval={2}
                  className="text-muted-foreground"
                />
                <YAxis 
                  yAxisId="temp"
                  tick={{ fontSize: 10 }}
                  domain={['dataMin - 5', 'dataMax + 5']}
                  className="text-muted-foreground"
                />
                <YAxis 
                  yAxisId="hum"
                  orientation="right"
                  tick={{ fontSize: 10 }}
                  domain={[0, 100]}
                  className="text-muted-foreground"
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Line 
                  yAxisId="temp"
                  type="monotone" 
                  dataKey="temperatura" 
                  stroke="hsl(var(--warning))" 
                  strokeWidth={2}
                  dot={false}
                  name="Temperatura (°C)"
                />
                <Line 
                  yAxisId="hum"
                  type="monotone" 
                  dataKey="humedad" 
                  stroke="hsl(var(--info))" 
                  strokeWidth={2}
                  dot={false}
                  name="Humedad (%)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-2 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-3 h-0.5 bg-warning rounded" />
              Temperatura (°C)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-0.5 bg-info rounded" />
              Humedad (%)
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Gráfica de Probabilidad de Lluvia */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CloudRain className="w-4 h-4 text-accent" />
            Probabilidad de Lluvia y Precipitación
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="hour" 
                  tick={{ fontSize: 10 }}
                  interval={2}
                  className="text-muted-foreground"
                />
                <YAxis 
                  yAxisId="prob"
                  tick={{ fontSize: 10 }}
                  domain={[0, 100]}
                  className="text-muted-foreground"
                />
                <YAxis 
                  yAxisId="precip"
                  orientation="right"
                  tick={{ fontSize: 10 }}
                  domain={[0, 'dataMax + 2']}
                  className="text-muted-foreground"
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Area 
                  yAxisId="prob"
                  type="monotone" 
                  dataKey="lluvia" 
                  fill="hsl(var(--accent) / 0.3)" 
                  stroke="hsl(var(--accent))"
                  strokeWidth={2}
                  name="Prob. lluvia (%)"
                />
                <Bar 
                  yAxisId="precip"
                  dataKey="precipitacion" 
                  fill="hsl(var(--info))" 
                  name="Precipitación (mm)"
                  radius={[2, 2, 0, 0]}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-2 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-accent/50 rounded" />
              Prob. lluvia (%)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-info rounded" />
              Precipitación (mm)
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de horas clave */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Detalle por Horas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
            {hourlyData
              .filter((_, i) => i % 3 === 0) // Cada 3 horas
              .map((h) => {
                const Icon = weatherIcons[h.icon] || Sun;
                return (
                  <div 
                    key={h.hour}
                    className="p-2 rounded-lg bg-muted/50 text-center hover:bg-muted transition-colors"
                  >
                    <p className="text-xs font-medium text-muted-foreground">
                      {h.hour.toString().padStart(2, '0')}:00
                    </p>
                    <Icon className="w-5 h-5 mx-auto my-1 text-accent" />
                    <p className="text-sm font-semibold">{h.temperature}°</p>
                    <div className="flex items-center justify-center gap-0.5 text-xs text-info">
                      <Droplets className="w-3 h-3" />
                      <span>{h.precipitationProbability}%</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Weather() {
  const [selectedMunicipality, setSelectedMunicipality] = useState<string>('');
  const [selectedGardenId, setSelectedGardenId] = useState<string>('');

  // Obtener jardines reales de la API
  const { data: gardensData, isLoading: gardensLoading, error: gardensError } = useQuery({
    queryKey: ['gardens'],
    queryFn: async () => {
      try {
        const response = await gardensService.getAll({ pageSize: 100 });
        if (!response.success) {
          console.error('[Weather] Error loading gardens:', response.error);
          throw new Error(response.error?.message || 'Error al cargar jardines');
        }
        return response.data;
      } catch (err) {
        console.error('[Weather] Error fetching gardens:', err);
        throw err;
      }
    },
    retry: 1,
  });

  // Obtener jardines y municipios únicos de forma segura
  const gardens = useMemo(() => {
    return Array.isArray(gardensData?.items) ? gardensData.items : [];
  }, [gardensData]);

  const municipalities = useMemo(() => {
    return [...new Set(
      gardens
        .map(g => g?.municipality)
        .filter((m): m is string => Boolean(m))
    )];
  }, [gardens]);

  // Seleccionar el primer municipio por defecto
  useEffect(() => {
    if (municipalities.length > 0 && !selectedMunicipality) {
      setSelectedMunicipality(municipalities[0]);
      const firstGarden = gardens.find(g => g.municipality === municipalities[0]);
      if (firstGarden) {
        setSelectedGardenId(firstGarden.id);
      }
    }
  }, [municipalities, gardens, selectedMunicipality]);

  // Actualizar jardín seleccionado cuando cambia el municipio
  useEffect(() => {
    if (selectedMunicipality) {
      const gardenInMunicipality = gardens.find(g => g.municipality === selectedMunicipality);
      if (gardenInMunicipality) {
        setSelectedGardenId(gardenInMunicipality.id);
      }
    }
  }, [selectedMunicipality, gardens]);

  // Obtener datos meteorológicos del jardín seleccionado (pronóstico 7 días)
  const { data: weatherData, isLoading: weatherLoading, refetch: refetchWeather } = useQuery({
    queryKey: ['weather', selectedGardenId],
    queryFn: async () => {
      if (!selectedGardenId) return null;
      try {
        const response = await gardensService.getWeather(selectedGardenId, 7);
        if (!response.success) {
          console.warn('Weather endpoint not available');
          return null;
        }
        return response.data;
      } catch (e) {
        console.warn('Weather endpoint error:', e);
        return null;
      }
    },
    enabled: !!selectedGardenId,
    retry: false,
  });

  // Obtener datos actuales + próximas horas
  const { data: currentWeather } = useQuery({
    queryKey: ['weather-current', selectedGardenId],
    queryFn: async () => {
      if (!selectedGardenId) return null;
      try {
        const response = await gardensService.getWeatherCurrent(selectedGardenId);
        if (!response.success) return null;
        return response.data;
      } catch (e) {
        console.warn('Weather current endpoint error:', e);
        return null;
      }
    },
    enabled: !!selectedGardenId,
    retry: false,
  });

  // Obtener datos horarios completos del día actual (todas las horas 0-23)
  const { data: hourlyWeather } = useQuery({
    queryKey: ['weather-hourly', selectedGardenId],
    queryFn: async () => {
      if (!selectedGardenId) return null;
      try {
        const response = await gardensService.getWeatherHourly(selectedGardenId);
        if (!response.success) return null;
        return response.data;
      } catch (e) {
        console.warn('Weather hourly endpoint error:', e);
        return null;
      }
    },
    enabled: !!selectedGardenId,
    retry: false,
  });

  // Convertir datos horarios del API al formato HourlyWeatherData
  const todayHourlyData: HourlyWeatherData[] | undefined = hourlyWeather?.hourlyData?.map((h) => ({
    hour: h.hour,
    temperature: h.temperature,
    humidity: h.humidity,
    precipitationProbability: h.precipitationProbability ?? 0,
    precipitation: 0, // API no devuelve este campo por hora
    windSpeed: 0, // API no devuelve este campo por hora
    description: h.description || '',
    icon: h.icon || '',
  }));

  // Convertir datos de la API a formato esperado (según API_REFERENCE.md)
  const municipalityWeather: WeatherData[] = weatherData?.forecast?.map((day, index) => {
    const precipProb = day.precipitationProbability ?? 0;
    return {
      municipality: weatherData.municipality || selectedMunicipality,
      date: new Date(day.date),
      temperature: day.temperature || { min: 0, max: 0 },
      humidity: day.humidity || 0,
      precipitationProbability: precipProb,
      precipitation: day.precipitation ?? 0,
      windSpeed: day.windSpeed ?? 0,
      description: day.description || '',
      icon: precipProb > 50 ? 'cloud-rain' : precipProb > 20 ? 'cloud' : 'sun',
      // Solo el día 0 (hoy) tiene datos horarios del endpoint /weather/hourly
      hourlyData: index === 0 ? todayHourlyData : undefined,
    };
  }) || [];

  const today = municipalityWeather[0];
  const tomorrow = municipalityWeather[1];

  // Loading state
  if (gardensLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  // Error state
  if (gardensError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Error al cargar jardines: {gardensError instanceof Error ? gardensError.message : 'Error desconocido'}
          <br />
          <span className="text-xs text-muted-foreground mt-2 block">
            El backend puede estar teniendo problemas. Verifica los logs del servidor.
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  // No gardens state
  if (gardens.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No hay jardines configurados. Crea un jardín primero para ver datos meteorológicos.
        </AlertDescription>
      </Alert>
    );
  }

  // No municipalities state
  if (municipalities.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Los jardines no tienen municipios asignados. Edita los jardines para añadir municipios y poder ver datos meteorológicos.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Meteorología
          </h1>
          <p className="text-muted-foreground mt-1">
            Datos meteorológicos de AEMET para tus jardines
          </p>
        </div>
        <Button 
          variant="outline" 
          className="gap-2"
          onClick={() => refetchWeather()}
          disabled={weatherLoading}
        >
          <RefreshCw className={`w-4 h-4 ${weatherLoading ? 'animate-spin' : ''}`} />
          Actualizar datos
        </Button>
      </div>

      {/* Municipality selector */}
      <div className="flex flex-wrap gap-2">
        {municipalities.map((municipality) => (
          <Button
            key={municipality}
            variant={selectedMunicipality === municipality ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedMunicipality(municipality)}
            className="gap-2"
          >
            <MapPin className="w-4 h-4" />
            {municipality}
            <Badge variant="secondary" className="ml-1">
              {gardens.filter(g => g.municipality === municipality).length}
            </Badge>
          </Button>
        ))}
      </div>

      {/* Loading weather */}
      {weatherLoading && (
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center gap-2">
              <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
              <span className="text-muted-foreground">Cargando datos meteorológicos...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current weather summary */}
      {today && (
        <motion.div variants={itemVariants}>
          <Card className="overflow-hidden">
            <CardHeader className="pb-2 gradient-hero text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <CardTitle className="text-lg font-display">
                    {selectedMunicipality} - Hoy
                  </CardTitle>
                </div>
                <Badge variant="secondary" className="bg-white/20 text-white border-0">
                  {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Current conditions */}
                <div className="flex items-center gap-6">
                  <div className="p-4 rounded-2xl bg-accent/10">
                    {(() => {
                      const WeatherIcon = weatherIcons[today.icon] || Sun;
                      return <WeatherIcon className="w-12 h-12 text-accent" />;
                    })()}
                  </div>
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-display font-bold">
                        {today.temperature.max}°
                      </span>
                      <span className="text-2xl text-muted-foreground">
                        / {today.temperature.min}°
                      </span>
                    </div>
                    <p className="text-lg text-foreground/80">{today.description}</p>
                  </div>
                </div>

                {/* Details grid */}
                <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg bg-muted/50 text-center">
                    <Droplets className="w-5 h-5 mx-auto text-info mb-1" />
                    <p className="text-lg font-semibold">{today.humidity}%</p>
                    <p className="text-xs text-muted-foreground">Humedad</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 text-center">
                    <Wind className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
                    <p className="text-lg font-semibold">{today.windSpeed}</p>
                    <p className="text-xs text-muted-foreground">km/h</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 text-center">
                    <CloudRain className="w-5 h-5 mx-auto text-accent mb-1" />
                    <p className="text-lg font-semibold">{today.precipitationProbability}%</p>
                    <p className="text-xs text-muted-foreground">Prob. lluvia</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 text-center">
                    <Thermometer className="w-5 h-5 mx-auto text-warning mb-1" />
                    <p className="text-lg font-semibold">{today.precipitation}</p>
                    <p className="text-xs text-muted-foreground">mm precip.</p>
                  </div>
                </div>
              </div>

              {/* Alert if rain probability is high */}
              {today.precipitationProbability > 50 && (
                <div className="mt-4 p-3 rounded-lg bg-info/10 border border-info/20 flex items-center gap-3">
                  <CloudRain className="w-5 h-5 text-info flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-info">
                      Alta probabilidad de lluvia
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Los triggers de lluvia podrían activarse automáticamente
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Quick preview: Next hours from /weather/current */}
      {currentWeather?.nextHours && currentWeather.nextHours.length > 0 && (
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                Próximas horas - {selectedMunicipality}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 overflow-x-auto pb-2">
                {/* Current hour */}
                <div className="flex-shrink-0 p-3 rounded-lg bg-primary/10 border border-primary/20 text-center min-w-[80px]">
                  <p className="text-xs text-muted-foreground mb-1">Ahora</p>
                  <p className="text-2xl font-bold">{currentWeather.temperature}°</p>
                  <p className="text-xs text-muted-foreground">{currentWeather.humidity}% hum</p>
                  {currentWeather.precipitationProbability !== null && currentWeather.precipitationProbability > 0 && (
                    <div className="flex items-center justify-center gap-1 mt-1 text-xs text-info">
                      <Droplets className="w-3 h-3" />
                      <span>{currentWeather.precipitationProbability}%</span>
                    </div>
                  )}
                </div>
                {/* Next hours */}
                {currentWeather.nextHours.map((h) => (
                  <div key={h.hour} className="flex-shrink-0 p-3 rounded-lg bg-muted/30 text-center min-w-[80px]">
                    <p className="text-xs text-muted-foreground mb-1">{h.hour.toString().padStart(2, '0')}:00</p>
                    <p className="text-2xl font-bold">{h.temperature}°</p>
                    <p className="text-xs text-muted-foreground">{h.humidity}% hum</p>
                    {h.precipitationProbability !== null && h.precipitationProbability !== undefined && h.precipitationProbability > 0 && (
                      <div className="flex items-center justify-center gap-1 mt-1 text-xs text-info">
                        <Droplets className="w-3 h-3" />
                        <span>{h.precipitationProbability}%</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Hourly forecast for today + Tomorrow summary */}
      <motion.div variants={itemVariants}>
        <Tabs defaultValue="today" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="today" className="gap-2">
              <Clock className="w-4 h-4" />
              Hoy por horas
            </TabsTrigger>
            <TabsTrigger value="tomorrow" className="gap-2">
              <ChevronRight className="w-4 h-4" />
              Previsión mañana
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="today" className="mt-4">
            {today?.hourlyData && today.hourlyData.length > 0 ? (
              <HourlyCharts 
                hourlyData={today.hourlyData} 
                title={`Previsión horaria para hoy - ${selectedMunicipality}`} 
              />
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No hay datos horarios completos disponibles para hoy</p>
                  {currentWeather?.nextHours && currentWeather.nextHours.length > 0 && (
                    <p className="text-xs mt-2 text-info">Los datos de las próximas horas se muestran en la sección superior</p>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="tomorrow" className="mt-4">
            {tomorrow ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-display flex items-center gap-2">
                    <ChevronRight className="w-5 h-5 text-primary" />
                    Previsión para mañana - {selectedMunicipality}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Resumen principal */}
                    <div className="flex items-center gap-6">
                      <div className="p-4 rounded-2xl bg-accent/10">
                        {(() => {
                          const WeatherIcon = weatherIcons[tomorrow.icon] || Sun;
                          return <WeatherIcon className="w-12 h-12 text-accent" />;
                        })()}
                      </div>
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-display font-bold">
                            {tomorrow.temperature.max}°
                          </span>
                          <span className="text-xl text-muted-foreground">
                            / {tomorrow.temperature.min}°
                          </span>
                        </div>
                        <p className="text-muted-foreground">{tomorrow.description || 'Sin descripción'}</p>
                      </div>
                    </div>

                    {/* Detalles */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 rounded-lg bg-muted/50 text-center">
                        <Droplets className="w-5 h-5 mx-auto text-info mb-1" />
                        <p className="text-lg font-semibold">{tomorrow.humidity}%</p>
                        <p className="text-xs text-muted-foreground">Humedad</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50 text-center">
                        <CloudRain className="w-5 h-5 mx-auto text-accent mb-1" />
                        <p className="text-lg font-semibold">{tomorrow.precipitationProbability}%</p>
                        <p className="text-xs text-muted-foreground">Prob. lluvia</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50 text-center">
                        <Wind className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
                        <p className="text-lg font-semibold">{tomorrow.windSpeed || 0}</p>
                        <p className="text-xs text-muted-foreground">km/h viento</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50 text-center">
                        <Thermometer className="w-5 h-5 mx-auto text-warning mb-1" />
                        <p className="text-lg font-semibold">{tomorrow.precipitation || 0}</p>
                        <p className="text-xs text-muted-foreground">mm precip.</p>
                      </div>
                    </div>
                  </div>

                  {/* Alerta si probabilidad de lluvia alta */}
                  {tomorrow.precipitationProbability > 50 && (
                    <div className="mt-4 p-3 rounded-lg bg-info/10 border border-info/20 flex items-center gap-3">
                      <CloudRain className="w-5 h-5 text-info flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-info">
                          Alta probabilidad de lluvia mañana
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Considera ajustar las programaciones de riego
                        </p>
                      </div>
                    </div>
                  )}

                  <p className="mt-4 text-xs text-muted-foreground text-center">
                    * AEMET solo proporciona datos horarios detallados para el día actual
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <ChevronRight className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No hay datos disponibles para mañana</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Forecast for next days */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-display">
            Previsión para los próximos días - {selectedMunicipality}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {municipalityWeather.slice(0, 5).map((day, index) => {
              const Icon = weatherIcons[day.icon] || Sun;
              const date = new Date(day.date);
              
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="p-4 rounded-xl bg-muted/30 text-center hover:bg-muted/50 transition-colors"
                >
                  <p className="text-sm font-medium text-muted-foreground mb-2">
                    {index === 0 ? 'Hoy' : index === 1 ? 'Mañana' : date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' })}
                  </p>
                  <Icon className="w-8 h-8 mx-auto text-accent mb-2" />
                  <p className="text-lg font-display font-bold">
                    {day.temperature.max}°
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {day.temperature.min}°
                  </p>
                  <div className="flex items-center justify-center gap-1 mt-2 text-xs text-info">
                    <Droplets className="w-3 h-3" />
                    <span>{day.precipitationProbability}%</span>
                  </div>
                  {day.hourlyData && (
                    <Badge variant="outline" className="mt-2 text-xs">
                      Por horas
                    </Badge>
                  )}
                </motion.div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Info about AEMET */}
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground text-center">
            Datos proporcionados por la{' '}
            <a 
              href="https://www.aemet.es" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Agencia Estatal de Meteorología (AEMET)
            </a>
            . Última actualización: {new Date().toLocaleString('es-ES')}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}
